import { useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@/api/client';
import { useManualProposal } from '@/api/queries';
import { proposalPath } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button } from '@/components/Button';
import { HealthBadge } from '@/components/badges';
import { Dialog } from '@/components/Dialog';
import { Field, Select, Textarea } from '@/components/fields';
import { Notice } from '@/components/primitives';
import { HEALTH_LABEL } from '@/domain/health';
import { HEALTHS, type ComponentView, type Health } from '@/domain/types';

import { ApproverField, DecisionFields, ErrorSummary } from './decision';
import { emptyDecision, explainError, useApproverName } from './decisionState';

/**
 * A manual proposal (TDD §5.4): born approved and published at once. The dialog says plainly
 * that manual changes do not stick (§5.6) and points at maintenance for holding a status.
 */
export function ManualProposalDialog({
  component,
  open,
  onClose,
}: {
  component: ComponentView;
  open: boolean;
  onClose: () => void;
}) {
  const { state, config } = component;
  const [toStatus, setToStatus] = useState<Health | ''>('');
  const [decision, setDecision] = useState(emptyDecision());
  const [note, setNote] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useManualProposal(state.scope);
  const toast = useToast();

  const choices = HEALTHS.filter((h) => h !== state.published_status);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!toStatus) local.to_status = 'Choose the status the page should show.';
    if (!note.trim()) local.note = 'Give a reason.';
    if (!approver.name.trim()) local.approver = 'Enter your name.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        scope: state.scope,
        capability_id: state.capability_id,
        to_status: toStatus as Health,
        incident_action: decision.incident_action,
        ...(decision.incident_title ? { incident_title: decision.incident_title } : {}),
        ...(decision.incident_body ? { incident_body: decision.incident_body } : {}),
        approver: approver.name,
        note,
      });
      toast.push({
        tone: 'success',
        title: `Published: ${config.display_name} now shows ${HEALTH_LABEL[result.proposal.to_status]}`,
      });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.publishFailed) {
        const failed = error.publishFailed;
        toast.push({
          tone: 'error',
          title: `The publish failed: ${failed.last_error}`,
          description: 'It is in the approval queue with Retry now.',
        });
        setMessage(null);
        onClose();
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Change ${config.display_name} by hand`}
      description={
        <>
          The page shows <HealthBadge health={state.published_status} variant="text" size="sm" />.
          Publishes at once.
        </>
      }
      busy={mutation.isPending}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Publish change
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Status to publish" required error={fieldErrors.to_status}>
          {(props) => (
            <Select
              {...props}
              name="to_status"
              value={toStatus}
              onChange={(e) => setToStatus(e.target.value as Health | '')}
              disabled={mutation.isPending}
            >
              <option value="">Choose a status…</option>
              {choices.map((h) => (
                <option key={h} value={h}>
                  {HEALTH_LABEL[h]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {toStatus === 'maintenance' ? (
          <Notice tone="info">
            Nothing is proposed for this component while it is Under Maintenance.
          </Notice>
        ) : toStatus ? (
          <Notice tone="warning">
            Manual changes do not stick: if the evidence disagrees, a new proposal follows. Use
            Under Maintenance to hold a status.
          </Notice>
        ) : null}

        <DecisionFields
          scope={state.scope}
          values={decision}
          onChange={setDecision}
          errors={fieldErrors}
          showResolution={false}
          disabled={mutation.isPending}
        />

        <Field label="Note" required error={fieldErrors.note}>
          {(props) => (
            <Textarea
              {...props}
              name="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Planned certificate rotation 11:00–12:00 UTC."
              disabled={mutation.isPending}
            />
          )}
        </Field>

        <ApproverField
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
        {mutation.isSuccess && mutation.data ? (
          <p className="text-body">
            <Link to={proposalPath(mutation.data.proposal.proposal_id)}>Open the proposal</Link>
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
